/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import { useSaleSettings } from '@features/sales/hooks/useSaleSettings';
import { useSalePaperworkMilestoneTypes } from '@features/sales/hooks/useSalePaperworkMilestoneTypes';
import { salesKeys } from '@features/sales/hooks/sales-keys';
import type { SaleDocumentCategory } from '@features/sales/types/sale.types';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { Text, TextInput, UnstyledButton } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import {
  errorClasses, fieldsetClasses, labelClasses, submitBtnClasses,
} from './dialog-styles';

import { PaperworkMilestonesSection } from './PaperworkMilestonesSection';
import { BlockMid, SettingsBlock } from './blocks';
import { BlockError, BlockLoading } from './BlockState';
import { DocumentCategoryTiles } from './DocumentCategoryTiles';
import {
  SettingsItemList, summaryOf, type ItemSectionProps, type ItemSummaries,
} from './SettingsItemList';
import { useRegisterSettingsDirty } from '../hooks/use-settings-dirty';
import { itemsOf, type SettingsItemKey } from '../constants/settings-items';
import {
  blockCaptionStyle, blockCardStyle, blockHeaderStyle, blockHeaderTextStyle, blockTitleStyle,
} from './settings-block-styles';

/** The seven ids the backend seeds, which i18n still translates by id. */
const BUILT_IN_CATEGORY_IDS = new Set([
  'deed', 'itbi', 'registry', 'commission_receipt',
  'contract', 'cancellation_deed', 'other',
]);

const SALE_BLOCKS: Partial<Record<SettingsItemKey, ComponentType>> = {
  saleCommission: DefaultCommissionBlock,
  saleMilestones: PaperworkMilestonesSection,
  saleCategories: DocumentCategoriesBlock,
};

/**
 * Everything about Vendas an agency configures: what a new sale's
 * commission starts from, which paperwork milestones it can record a date
 * against, and what its documents are filed under.
 *
 * Opens on a list with each setting's current value; a row opens that
 * setting alone.
 *
 * @returns The sale settings section.
 */
export function SaleSettingsSection({ item = null, onOpenItem = () => undefined }: ItemSectionProps) {
  const summaries = useSaleSummaries();
  const Block = item ? SALE_BLOCKS[item] : undefined;
  if (Block) return <Block />;
  return <SettingsItemList items={itemsOf('vendas')} summaries={summaries} onOpen={onOpenItem} />;
}

/**
 * Each sale setting's current value, in the words the list shows.
 *
 * @returns The summaries keyed by item.
 */
function useSaleSummaries(): ItemSummaries {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const sale = useSaleSettings(agency.id ?? '');
  const milestones = useSalePaperworkMilestoneTypes(agency.id ?? '');

  return {
    saleCommission: summaryOf(sale, () => (sale.defaultCommissionPercentage === null
      ? t('v2.items.saleCommission.none')
      : `${sale.defaultCommissionPercentage}%`)),
    saleMilestones: summaryOf(milestones, () => t('v2.items.saleMilestones.summary', {
      count: milestones.types.length,
      required: milestones.types.filter((type) => type.required).length,
    })),
    saleCategories: summaryOf(sale, () => t('v2.items.categoryCount', { count: sale.documentCategories.length })),
  };
}

/** The result of parsing and validating the commission-percentage draft. */
type PercentParse = { ok: true; value: number | null } | { ok: false };

/**
 * Whether *value* is a valid commission percentage (0–100).
 *
 * @param value - The parsed percentage.
 * @returns True when it is a number within range.
 */
function isValidPercentage(value: number): boolean {
  return !Number.isNaN(value) && value >= 0 && value <= 100;
}

/**
 * Parses the commission draft: blank means "unset" (null), otherwise it must
 * be a percentage between 0 and 100.
 *
 * @param draft - The raw text from the commission field.
 * @returns The parsed value (or null), or a failure when out of range.
 */
function parseCommissionPercentage(draft: string): PercentParse {
  const trimmed = draft.trim();
  if (trimmed === '') return { ok: true, value: null };
  const value = Number(trimmed);
  return isValidPercentage(value) ? { ok: true, value } : { ok: false };
}

/**
 * The percentage a new sale starts from.
 *
 * Empty is not zero: an empty field means the agency never chose one and a
 * sale starts at 0 as before, while a typed 0 is a sale with no commission.
 * Both reach the backend distinctly — empty as null, zero as zero.
 *
 * One field does not deserve a card of its own, so it sits in the block's
 * header, on the right of the words that explain it.
 *
 * @returns The default-commission block.
 */
function DefaultCommissionBlock() {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const queryClient = useQueryClient();
  const { canManageSalePaperworkMilestones: canManage } = useAgencyPermissions();
  const { defaultCommissionPercentage, setDefaultCommissionPercentage, isLoading, isError } =
    useSaleSettings(agency.id ?? '');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // The field mirrors what is stored until the user types; a stored 0 shows
  // as "0" instead of an empty field.
  useEffect(() => {
    setDraft(defaultCommissionPercentage === null ? '' : String(defaultCommissionPercentage));
  }, [defaultCommissionPercentage]);

  const persistCommission = async (value: number | null) => {
    setSaving(true);
    try {
      await setDefaultCommissionPercentage(value);
      notifications.show({ color: 'green', message: t('v2.vendas.commission.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.vendas.commission.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const parsed = parseCommissionPercentage(draft);
    if (!parsed.ok) {
      notifications.show({ color: 'red', message: t('v2.vendas.commission.invalid') });
      return;
    }
    await persistCommission(parsed.value);
  };

  // The stored value, in the same shape the field holds it, so "dirty" is a
  // string comparison and a reformatted-but-identical number does not count
  // as a change.
  const stored = defaultCommissionPercentage === null ? '' : String(defaultCommissionPercentage);

  useRegisterSettingsDirty({
    isDirty: canManage && draft !== stored,
    isSaving: saving,
    onSave: () => void handleSave(),
    onDiscard: () => setDraft(stored),
  });

  if (isError) {
    return (
      <SettingsBlock title={t('v2.vendas.commission.title')} testId="sale-commission-block">
        <BlockError onRetry={() => void queryClient.refetchQueries({ queryKey: salesKeys.settings(agency.id ?? '') })} />
      </SettingsBlock>
    );
  }

  return (
    <section style={blockCardStyle} data-testid="sale-commission-block">
      <div style={{ ...blockHeaderStyle, alignItems: 'center', borderBottom: 'none' }}>
        <div style={blockHeaderTextStyle}>
          <Text as="label" htmlFor="default-commission-percentage" style={blockTitleStyle}>
            {t('v2.vendas.commission.title')}
          </Text>
          <Text component="span" id="default-commission-help" style={blockCaptionStyle}>
            {t('v2.vendas.commission.explanation')}
          </Text>
        </div>
        <div className="flex items-center gap-(--space-2)" style={{ flex: 'none' }}>
          <div style={{ width: 96 }}>
            <TextInput
              id="default-commission-percentage"
              name="default-commission-percentage"
              value={draft}
              placeholder={isLoading ? '' : t('v2.vendas.commission.placeholder')}
              disabled={!canManage || isLoading}
              onChange={(e) => setDraft(e.target.value)}
              aria-describedby="default-commission-help"
              data-testid="default-commission-input"
            />
          </div>
          <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
            %
          </Text>
        </div>
      </div>
    </section>
  );
}

/**
 * The vocabulary a sale's documents are filed under.
 *
 * Adding and renaming only: a document already filed names its category
 * id, so removing the category would orphan that reference — the same
 * reason the paperwork milestones have no removal either.
 *
 * @returns The document-categories block.
 */
function DocumentCategoriesBlock() {
  const { t } = useTranslation('features/agency-settings');
  const { t: tSales } = useTranslation('features/sales');
  const { agency } = useAgency((state) => state);
  const queryClient = useQueryClient();
  const { canManageSalePaperworkMilestones: canManage } = useAgencyPermissions();
  const {
    documentCategories, addDocumentCategory, renameDocumentCategory, isLoading, isError,
  } = useSaleSettings(agency.id ?? '');
  const [editing, setEditing] = useState<SaleDocumentCategory | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  /** Built-in ids stay translated; an agency's own category shows its own label. */
  const categoryLabel = (category: SaleDocumentCategory) =>
    (BUILT_IN_CATEGORY_IDS.has(category.id)
      ? tSales(`documents.categories.${category.id}`, { defaultValue: category.label })
      : category.label);

  const renderBody = () => {
    if (isLoading) return <BlockLoading rows={3} />;
    if (isError) {
      return <BlockError onRetry={() => void queryClient.refetchQueries({ queryKey: salesKeys.settings(agency.id ?? '') })} />;
    }
    return (
      <BlockMid>
        <DocumentCategoryTiles
          categories={documentCategories}
          labelOf={categoryLabel}
          canManage={canManage}
          onRename={setEditing}
          emptyText={t('v2.vendas.categories.empty')}
          testId="sale-document-categories"
        />
      </BlockMid>
    );
  };

  return (
    <SettingsBlock
      title={t('v2.vendas.categories.title')}
      caption={t('v2.vendas.categories.explanation')}
      action={canManage && !isLoading
        ? { label: t('v2.vendas.categories.add'), onClick: () => setIsAdding(true), testId: 'sale-category-add' }
        : undefined}
      note={t('v2.vendas.categories.note')}
      testId="sale-categories-block"
    >
      {renderBody()}

      {isAdding && (
        <CategoryModal
          category={null}
          onClose={() => setIsAdding(false)}
          onSubmit={async (label) => {
            await addDocumentCategory(label);
            setIsAdding(false);
          }}
        />
      )}

      {editing && (
        <CategoryModal
          category={editing}
          initialLabel={categoryLabel(editing)}
          onClose={() => setEditing(null)}
          onSubmit={async (label) => {
            await renameDocumentCategory(editing.id, label);
            setEditing(null);
          }}
        />
      )}
    </SettingsBlock>
  );
}

interface CategoryModalProps {
  /** Null when adding; the category being renamed otherwise. */
  category: SaleDocumentCategory | null;
  initialLabel?: string;
  onClose: () => void;
  onSubmit: (label: string) => Promise<void>;
}

/**
 * Add/rename modal for one document category — plain local state, the same
 * convention `PaperworkMilestonesSection`'s own modal uses.
 *
 * @returns The modal element.
 */
function CategoryModal({ category, initialLabel, onClose, onSubmit }: CategoryModalProps) {
  const { t } = useTranslation('features/agency-settings');
  const [label, setLabel] = useState(initialLabel ?? '');
  const [labelError, setLabelError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!label.trim()) {
      setLabelError(t('v2.vendas.categories.labelRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(label.trim());
      notifications.show({ color: 'green', message: t('v2.vendas.categories.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.vendas.categories.saveError') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdaptiveFormShell
      open
      onClose={onClose}
      title={category ? t('v2.vendas.categories.editTitle') : t('v2.vendas.categories.addTitle')}
      density="compact"
      footer={
        <UnstyledButton
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={submitBtnClasses}
          data-testid="category-modal-submit"
        >
          {submitting ? t('v2.vendas.categories.saving') : t('v2.vendas.categories.save')}
        </UnstyledButton>
      }
    >
      <div data-testid="category-form" className="flex flex-col gap-(--space-4)">
        <fieldset className={fieldsetClasses}>
          <Text as="label" htmlFor="category-label" className={labelClasses}>
            {t('v2.vendas.categories.labelField')}
          </Text>
          <TextInput
            id="category-label"
            name="category-label"
            value={label}
            placeholder={t('v2.vendas.categories.labelPlaceholder')}
            onChange={(e) => { setLabel(e.target.value); setLabelError(''); }}
            autoFocus
          />
          {labelError && <Text component="span" className={errorClasses}>{labelError}</Text>}
        </fieldset>
      </div>
    </AdaptiveFormShell>
  );
}
